import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Check, Move, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

interface ImageCropEditorProps {
  imageFile: File;
  onSave: (croppedBlob: Blob) => Promise<void>;
  onCancel: () => void;
  aspectRatio?: string; // e.g., "16/9", "1/1", "16/6"
  hint?: string;
}

export function ImageCropEditor({ 
  imageFile, 
  onSave, 
  onCancel, 
  aspectRatio = '16/9',
  hint = 'This will be your cover image'
}: ImageCropEditorProps) {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  
  // Pinch-to-zoom state
  const [isPinching, setIsPinching] = useState(false);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialPinchZoom, setInitialPinchZoom] = useState(1);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Load the image from file
  useEffect(() => {
    const url = URL.createObjectURL(imageFile);
    setImageUrl(url);
    
    // Load image to get dimensions
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
    img.src = url;
    
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // Get container dimensions
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    }
  }, []);

  // Calculate base scaled dimensions to cover the container (zoom = 1)
  const getBaseScaledDimensions = useCallback(() => {
    if (!imageSize.width || !imageSize.height || !containerSize.width || !containerSize.height) {
      return { width: 0, height: 0 };
    }

    const containerRatio = containerSize.width / containerSize.height;
    const imageRatio = imageSize.width / imageSize.height;

    if (imageRatio > containerRatio) {
      // Image is wider - scale by height
      const scaledHeight = containerSize.height;
      const scaledWidth = scaledHeight * imageRatio;
      return { width: scaledWidth, height: scaledHeight };
    } else {
      // Image is taller - scale by width
      const scaledWidth = containerSize.width;
      const scaledHeight = scaledWidth / imageRatio;
      return { width: scaledWidth, height: scaledHeight };
    }
  }, [imageSize, containerSize]);

  // Get zoomed dimensions
  const getScaledDimensions = useCallback(() => {
    const base = getBaseScaledDimensions();
    return {
      width: base.width * zoom,
      height: base.height * zoom
    };
  }, [getBaseScaledDimensions, zoom]);

  // Clamp position to valid bounds
  const clampPosition = useCallback((x: number, y: number) => {
    const scaled = getScaledDimensions();
    const maxX = Math.max(0, scaled.width - containerSize.width);
    const maxY = Math.max(0, scaled.height - containerSize.height);
    
    return {
      x: Math.max(-maxX, Math.min(0, x)),
      y: Math.max(-maxY, Math.min(0, y))
    };
  }, [getScaledDimensions, containerSize]);

  // Re-clamp position when zoom changes
  useEffect(() => {
    setPosition(prev => clampPosition(prev.x, prev.y));
  }, [zoom, clampPosition]);

  // Calculate distance between two touch points
  const getTouchDistance = (touches: TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 2) {
      // Start pinch-to-zoom
      setIsPinching(true);
      setIsDragging(false);
      const distance = getTouchDistance(e.nativeEvent.touches);
      setInitialPinchDistance(distance);
      setInitialPinchZoom(zoom);
    } else if (e.touches.length === 1 && !isPinching) {
      // Start drag
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setPosition(clampPosition(newX, newY));
  }, [isDragging, dragStart, clampPosition]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      // Handle pinch-to-zoom
      const currentDistance = getTouchDistance(e.touches);
      const scale = currentDistance / initialPinchDistance;
      const newZoom = Math.max(1, Math.min(3, initialPinchZoom * scale));
      setZoom(newZoom);
    } else if (e.touches.length === 1 && isDragging && !isPinching) {
      // Handle drag
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;
      setPosition(clampPosition(newX, newY));
    }
  }, [isDragging, isPinching, dragStart, clampPosition, initialPinchDistance, initialPinchZoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  }, []);

  // Add global event listeners for drag and pinch
  useEffect(() => {
    if (isDragging || isPinching) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, isPinching, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const handleZoomChange = (value: number[]) => {
    setZoom(value[0]);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 1));
  };

  const handleSave = async () => {
    if (!imageRef.current || !containerRef.current) return;
    
    setIsSaving(true);
    
    try {
      const scaled = getScaledDimensions();
      
      // Create a canvas with the container dimensions
      const canvas = document.createElement('canvas');
      canvas.width = containerSize.width * 2; // 2x for retina
      canvas.height = containerSize.height * 2;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      // Load the original image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = imageUrl;
      });
      
      // Calculate source rectangle from original image
      const scaleX = imageSize.width / scaled.width;
      const scaleY = imageSize.height / scaled.height;
      
      const sourceX = -position.x * scaleX;
      const sourceY = -position.y * scaleY;
      const sourceWidth = containerSize.width * scaleX;
      const sourceHeight = containerSize.height * scaleY;
      
      // Draw the cropped portion
      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, canvas.width, canvas.height
      );
      
      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => b ? resolve(b) : reject(new Error('Failed to create blob')),
          'image/jpeg',
          0.9
        );
      });
      
      await onSave(blob);
    } catch (error) {
      console.error('Error saving cropped image:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const scaledDims = getScaledDimensions();

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          disabled={isSaving}
          className="text-white hover:bg-white/20 h-11 w-11"
        >
          <X className="w-6 h-6" />
        </Button>
        <div className="flex items-center gap-2 text-sm">
          <Move className="w-4 h-4" />
          <span>Drag to reposition</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSave}
          disabled={isSaving}
          className="text-white hover:bg-white/20 h-11 w-11"
        >
          {isSaving ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Check className="w-6 h-6" />
          )}
        </Button>
      </div>
      
      {/* Image Container */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div
          ref={containerRef}
          className="relative w-full max-w-lg overflow-hidden rounded-xl cursor-grab active:cursor-grabbing"
          style={{ aspectRatio }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Guide overlay */}
          <div className="absolute inset-0 border-2 border-white/50 rounded-xl pointer-events-none z-10" />
          
          {imageUrl && scaledDims.width > 0 && (
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Cover preview"
              className="absolute select-none"
              style={{
                width: scaledDims.width,
                height: scaledDims.height,
                transform: `translate(${position.x}px, ${position.y}px)`,
                maxWidth: 'none'
              }}
              draggable={false}
            />
          )}
        </div>
      </div>
      
      {/* Zoom Controls */}
      <div className="px-6 py-4">
        <div className="max-w-sm mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomOut}
            disabled={zoom <= 1}
            className="text-white hover:bg-white/20 shrink-0 h-11 w-11"
          >
            <ZoomOut className="w-5 h-5" />
          </Button>
          
          <Slider
            value={[zoom]}
            onValueChange={handleZoomChange}
            min={1}
            max={3}
            step={0.01}
            className="flex-1 [&_[role=slider]]:bg-white [&_[role=slider]]:border-0 [&_.relative]:bg-white/30 [&_[data-orientation=horizontal]>.bg-primary]:bg-white"
          />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            className="text-white hover:bg-white/20 shrink-0 h-11 w-11"
          >
            <ZoomIn className="w-5 h-5" />
          </Button>
        </div>
        
        <p className="text-center text-white/60 text-sm mt-3">
          {Math.round(zoom * 100)}% zoom
        </p>
      </div>
      
      {/* Footer hint */}
      <div className="pb-4 text-center text-white/60 text-sm">
        {hint}
      </div>
    </div>
  );
}